import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppDispatch } from '../../../../../../hooks/redux';
import { CustomMUIButton } from '../../../../buttons/CustomMUIButton';
import { ButtonClassNames, ModalVariants } from '../../../../../../models/Common';
import { ApiError } from '../../../../../../models/ApiError';
import { modalSlice } from '../../../../../../store/reducers/ModalSlice';
import { userInfoApi } from '../../../../../../services/apiServices/user/userInfo';
import { useCustomSnackbar } from '../../../../../../hooks/useCustomEnqueueSnackbar';
import { FormInputPassword } from '../../../../../inputs/FormInputPassword';
import { getErrorMessage } from '../../../../../../utils';

interface DeleteSmsPhoneProps {
  phoneNumber: string;
  handleClose: () => void;
}

interface IDeleteSmsPhone {
  phoneNumber: string;
  phoneId: number;
  currentPassword: string;
}

export const DeleteSmsPhone: FC<DeleteSmsPhoneProps> = ({ phoneNumber, handleClose }) => {
  const { t } = useTranslation();
  const methods = useForm<IDeleteSmsPhone>();
  const { setError, handleSubmit } = methods;
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const [deleteSmsPhoneApi, { isLoading }] = userInfoApi.useDeleteSmsPhoneMutation();
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;

  const handleDeletePhone = async ({ currentPassword }) => {
    try {
      await deleteSmsPhoneApi({ currentPassword })
        .unwrap()
        .then(() => {
          customEnqueueSnackbar(t('snackbar.mainPhoneNumberDeleted'), 'warning');
          handleClose();
        });
    } catch (err) {
      const error = err as ApiError;
      if (error?.data?.error?.errors) {
        const apiErrors = error.data.error.errors as Partial<Record<keyof IDeleteSmsPhone, string>>;
        const apiErrorsKeys = Object.keys(apiErrors) as Array<keyof IDeleteSmsPhone>;
        apiErrorsKeys.forEach((element) => {
          setError(element, {
            type: 'server',
            message: apiErrors[element],
          });
        });
      } else {
        const message = getErrorMessage(err);
        customEnqueueSnackbar(message, 'error');
      }
    }
  };

  const handleForgotPassword = () => {
    dispatch(openModal({ modalVariants: ModalVariants.LogOut }));
  };

  return (
    <div className="modals">
      <div className="modals__header">
        <article className="modals__header__title">{t('accountSettings.delete?')}</article>
        <p className="modals__header__description">
          {t('accountSettings.wantDeleteMainNumber')} <strong>+38{phoneNumber}</strong>?
        </p>
        <form className="modals__form" onSubmit={handleSubmit(handleDeletePhone)}>
          <FormProvider {...methods}>
            <FormInputPassword
              name="currentPassword"
              className="primary"
              label={t('auth.enterPassLk')}
              isRequired
              minLength={6}
              maxLength={16}
              fieldHasName
            />
            <div className="modals__btns-wrapper">
              <div className="modals__btn-group">
                <CustomMUIButton
                  label={t('buttons.delete')}
                  type="submit"
                  className={ButtonClassNames.PrimaryFulfilled}
                  isBold
                  isLoading={isLoading}
                />
              </div>
              <div className="modals__btn-group">
                <CustomMUIButton
                  label={t('auth.forgotMyPassword')}
                  handleClick={handleForgotPassword}
                  className={ButtonClassNames.SecondaryOutlined}
                  isBold
                  type="button"
                />
              </div>
            </div>
          </FormProvider>
        </form>
      </div>
    </div>
  );
};
